// 261010 P3-1 (luồng Sky) — logic THUẦN của khối "Storyboard" trên thẻ video: nhãn trạng thái, việc nền, bản nháp bảng khung, kiểm trước PUT, câu lỗi.
// KHÔNG import antd/React: dưới jest của react-scripts antd v6 + react-router v7 không load được (xem CLAUDE.md) ⇒ logic tách khỏi component
// (mẫu `positionMapUtils.ts`).
import type {
  FilmFailedJob, FilmRunningJob, Storyboard, StoryboardAuthorStatus, StoryboardDrift, StoryboardFrame, StoryboardListItem,
} from '../../types/film';
import { apiErrorText } from './apiErrorText';
import { approvalErrorText, approvalView, type ApprovalView, type StatusTag } from './positionMapUtils';

// ───────────────────────── Hằng số + từ vựng máy ─────────────────────────

export const STORYBOARD_JOB = 'FILM_STORYBOARD';
/** `detail.phase` của job (BE `startBg` meta.phase) — compose/generate cùng type + key, phân biệt bằng phase. */
export const STORYBOARD_PHASE = { COMPOSE: 'storyboard-compose', GENERATE: 'storyboard-generate' } as const;

export const FRAME_MIN = 2;
export const FRAME_MAX = 10;
export const GROUP_MAX = 4;
export const ACTION_MAX = 400;
export const WHO_MAX = 200;
/** Sai số tổng giờ các nhóm so với `gen_duration_s` (BE `EPSILON`). */
export const DURATION_EPS = 0.5;
const SAME_TIME_EPS = 0.00001;

export interface VocabOption { value: string; label: string; hint?: string }

/** 🔴 Khớp BE `filmCameraVocab` (SHOT_SIZES / CAMERA_ANGLES / CAMERA_MOVES) theo thứ tự `Object.keys`. Test khoá mảng — lệch BE là PUT bị 400 BAD_FRAMES. */
export const SHOT_SIZE_OPTIONS: VocabOption[] = [
  { value: 'EWS', label: 'EWS · cực rộng', hint: 'Extreme wide shot' },
  { value: 'WS', label: 'WS · rộng', hint: 'Wide shot' },
  { value: 'MWS', label: 'MWS · trung-rộng', hint: 'Medium-wide shot' },
  { value: 'MS', label: 'MS · trung', hint: 'Medium shot' },
  { value: 'MCU', label: 'MCU · cận trung', hint: 'Medium close-up' },
  { value: 'CU', label: 'CU · cận', hint: 'Close-up' },
  { value: 'ECU', label: 'ECU · cực cận', hint: 'Extreme close-up' },
];
export const ANGLE_OPTIONS: VocabOption[] = [
  { value: 'EYE', label: 'EYE · ngang tầm mắt' },
  { value: 'LOW', label: 'LOW · hắt từ dưới' },
  { value: 'HIGH', label: 'HIGH · chúc từ trên' },
  { value: 'BIRD', label: 'BIRD · từ trên cao' },
  { value: 'DUTCH', label: 'DUTCH · nghiêng' },
  { value: 'GROUND', label: 'GROUND · sát đất' },
];
export const MOVE_OPTIONS: VocabOption[] = [
  { value: 'STATIC', label: 'STATIC · cố định' },
  { value: 'PUSH_IN', label: 'PUSH_IN · đẩy vào' },
  { value: 'PULL_OUT', label: 'PULL_OUT · kéo ra' },
  { value: 'PAN', label: 'PAN · lia ngang' },
  { value: 'TRACKING', label: 'TRACKING · bám theo' },
  { value: 'ORBIT', label: 'ORBIT · xoay quanh' },
  { value: 'HANDHELD', label: 'HANDHELD · cầm tay' },
  { value: 'AERIAL', label: 'AERIAL · trên không' },
  { value: 'TILT_UP', label: 'TILT_UP · ngẩng máy' },
  { value: 'TILT_DOWN', label: 'TILT_DOWN · chúc máy' },
  { value: 'WHIP_PAN', label: 'WHIP_PAN · lia rất nhanh' },
  { value: 'DOLLY_ZOOM', label: 'DOLLY_ZOOM · dolly zoom' },
  { value: 'CRANE_DOWN', label: 'CRANE_DOWN · hạ crane' },
  { value: 'PAN_360', label: 'PAN_360 · lia trọn vòng' },
  { value: 'SNAP_ZOOM', label: 'SNAP_ZOOM · zoom giật' },
];
const codesOf = (opts: VocabOption[]): Set<string> => new Set(opts.map((o) => o.value));
const SHOT_SIZE_SET = codesOf(SHOT_SIZE_OPTIONS);
const ANGLE_SET = codesOf(ANGLE_OPTIONS);
const MOVE_SET = codesOf(MOVE_OPTIONS);

// ───────────────────────── Việc nền FILM_STORYBOARD ─────────────────────────

/** Tên việc theo `phase` của job (không phải tên type — type dùng chung cho soạn khung và gen tấm). */
export function storyboardJobName(phase?: string | null): string {
  if (phase === STORYBOARD_PHASE.COMPOSE) return 'Soạn khung';
  if (phase === STORYBOARD_PHASE.GENERATE) return 'Gen tấm';
  return 'Storyboard';
}

/**
 * Việc FILM_STORYBOARD đang chạy của event. Khớp theo `key` HOẶC `eventId`: khoá luôn theo eventId (`key`), còn `eventId` chỉ có khi job tự khai meta
 * (compose/generate có, PUT khung thì không).
 */
export const storyboardJobOf = (jobs: FilmRunningJob[], eventId: string): FilmRunningJob | null =>
  jobs.find((j) => j.type === STORYBOARD_JOB && (j.key === eventId || j.eventId === eventId)) || null;

/** Job storyboard có ở lượt poll TRƯỚC mà mất ở lượt NÀY ⇒ vừa xong (thành công hoặc lỗi) ⇒ nạp lại danh sách. Lượt đầu (null) ⇒ rỗng. */
export function finishedStoryboardJobs(prev: FilmRunningJob[] | null, next: FilmRunningJob[]): FilmRunningJob[] {
  if (!prev) return [];
  const nextIds = new Set(next.filter((j) => j.type === STORYBOARD_JOB).map((j) => j.id));
  return prev.filter((j) => j.type === STORYBOARD_JOB && !nextIds.has(j.id));
}

/**
 * Lỗi MỚI nhất của event này (bỏ lỗi đã đóng). Chỉ gắn được lỗi có `eventId`: compose/generate tự khai `eventId`; lỗi PUT khung không có meta nên
 * không bao giờ vào `/plans/:id/jobs` (BE lọc theo `detail.planId`/`key=planId`) — người sửa đã nhận lỗi ngay trên response.
 */
export function storyboardFailureOf(failures: FilmFailedJob[], eventId: string, dismissed: string[]): FilmFailedJob | null {
  const mine = failures.filter((f) => f.type === STORYBOARD_JOB && f.eventId === eventId && !dismissed.includes(f.id));
  if (!mine.length) return null;
  return mine.reduce((best, f) => (f.minutesAgo < best.minutesAgo ? f : best));
}

// ───────────────────────── Dữ liệu cấp tab ─────────────────────────

/** `GET /plans/:id/storyboards` là MẢNG mỗi event một phần tử; event chưa soạn chỉ có `{eventId, eventOrder}` ⇒ chỉ giữ event CÓ storyboard. */
export function storyboardsByEvent(list: StoryboardListItem[] | null | undefined): Record<string, Storyboard> {
  const out: Record<string, Storyboard> = {};
  if (!Array.isArray(list)) return out;
  for (const item of list) if (item?.storyboard) out[item.eventId] = item.storyboard;
  return out;
}

/** Đổi khi thứ làm lệch storyboard đổi (thời lượng gen, tập shot, tập giai đoạn) ⇒ nạp lại GET để thấy `drift` mới. Chữ khác thì không. */
export function eventsSignature(events: Array<{ id: string; genDurationS?: number | null; shots?: Array<{ id: string }> | null; videoMeta?: { phases?: Array<{ order: number }> | null } | null }>): string {
  return events
    .map((e) => `${e.id}:${e.genDurationS ?? ''}:${(e.shots || []).map((s) => s.id).join('.')}:${(e.videoMeta?.phases || []).map((p) => p.order).join('.')}`)
    .join('|');
}

// ───────────────────────── Nhãn trạng thái ─────────────────────────

const AUTHOR_TAG: Record<StoryboardAuthorStatus, { text: string; color: string; tip: string }> = {
  AI: { text: 'AI', color: 'default', tip: 'Khung do AI soạn, chưa ai sửa.' },
  EDITED: { text: 'đã sửa', color: 'blue', tip: 'Đã có người sửa khung — "AI soạn" lại sẽ hỏi xác nhận trước khi ghi đè.' },
  AGENT: { text: 'agent sửa', color: 'purple', tip: 'Agent (AI ngoài) đã tạo/sửa khung — "AI soạn" lại sẽ hỏi xác nhận trước khi ghi đè. Người sửa tiếp thì nhãn đổi thành "đã sửa".' },
};

/**
 * Nhãn trạng thái của khối, thứ tự: đang chạy → lỗi → (chưa có storyboard | chưa có tấm | cũ) → người sửa → lệch nguồn → chưa lưu.
 * "Cũ" do BE tính lúc đọc (`staleness`); ở đây chỉ dịch.
 */
export function storyboardTags(sb: Storyboard | null | undefined, st: { job?: FilmRunningJob | null; error?: string | null; dirty?: boolean }): StatusTag[] {
  const tags: StatusTag[] = [];
  if (st.job) {
    const name = st.job.phase === STORYBOARD_PHASE.COMPOSE || st.job.phase === STORYBOARD_PHASE.GENERATE ? storyboardJobName(st.job.phase).toLowerCase() : 'chạy';
    tags.push({ key: 'running', text: `đang ${name}`, color: 'processing', tip: 'Đang chạy ở nền — đừng bấm lại, gen tấm là một lần tính tiền.' });
  }
  if (st.error) tags.push({ key: 'error', text: 'lỗi', color: 'error', tip: st.error });
  if (!sb) {
    tags.push({ key: 'none', text: 'chưa có storyboard', color: 'default', tip: 'Bấm "AI soạn" để dựng danh sách khung từ shot, giai đoạn và sơ đồ của video này.' });
    return tags;
  }
  if (sb.staleness === 'NO_SHEET') tags.push({ key: 'nosheet', text: 'chưa có tấm', color: 'default', tip: 'Đã có khung nhưng chưa gen tấm ảnh.' });
  else if (sb.staleness === 'INPUT_CHANGED') tags.push({ key: 'stale', text: 'cũ · đầu vào đã đổi', color: 'warning', tip: 'Từ lúc gen tấm này, đầu vào đã đổi: chữ khung, chú thích, ảnh sơ đồ / sheet nhân vật / bối cảnh hoặc cỡ ảnh. Gen lại để tấm khớp.' });
  const a = AUTHOR_TAG[sb.authorStatus];
  if (a) tags.push({ key: 'author', text: a.text, color: a.color, tip: a.tip });
  const drift = driftLines(sb.drift || []);
  if (drift.length) tags.push({ key: 'drift', text: `lệch nguồn (${drift.length})`, color: 'warning', tip: drift.join('\n') });
  if (st.dirty) tags.push({ key: 'dirty', text: 'chưa lưu', color: 'orange', tip: 'Bảng khung có chỉnh sửa chưa lưu.' });
  return tags;
}

const DRIFT_REASON_TEXT: Record<string, string> = {
  SHOT_GONE: 'shot nguồn không còn trong video',
  PHASE_GONE: 'giai đoạn nguồn không còn',
  MAP_GONE: 'sơ đồ không còn hoặc không còn gán đúng giai đoạn của khung',
  // 261010 P2b-1: trước đây hiện mã thô `SUBJECT_FACING_CHANGED`.
  SUBJECT_FACING_CHANGED: 'hướng mặt chủ thể của shot nguồn đã đổi sau khi soạn khung',
};
const DURATION_TEXT = 'Thời lượng gen của video đã đổi so với tổng giờ các nhóm — chỉnh lại giờ các nhóm cho khớp.';

/** BE đẩy `DURATION_CHANGED` MỖI khung một lần ⇒ gộp thành MỘT dòng cấp storyboard; lý do còn lại gom theo lý do kèm số khung. */
export function driftLines(drift: StoryboardDrift[]): string[] {
  const lines: string[] = [];
  const byReason = new Map<string, Set<number>>();
  let duration = false;
  for (const d of drift) {
    if (d.reason === 'DURATION_CHANGED') { duration = true; continue; }
    byReason.set(d.reason, (byReason.get(d.reason) || new Set()).add(d.frameNo));
  }
  for (const [reason, frames] of Array.from(byReason.entries())) {
    const nos = Array.from(frames).sort((a, b) => a - b).join(', ');
    lines.push(`Khung ${nos}: ${DRIFT_REASON_TEXT[reason] || reason}.`);
  }
  if (duration) lines.push(DURATION_TEXT);
  return lines;
}

// ───────────────────────── Cảnh báo ảnh tham chiếu ─────────────────────────

/**
 * `refsWarnings` lẫn hai thứ: cảnh báo (vẫn gen được) và MÃ LỖI dựng đầu vào (BE `listForPlan` bắt lỗi `buildInput` rồi đẩy `e.code` vào đây:
 * thiếu plate, quá nhiều nhân vật, thiếu template…) — loại thứ hai CHẶN gen.
 */
const BLOCKING_WARNINGS = new Set(['NO_PLATE', 'TOO_MANY_CHARACTERS', 'NOT_FOUND', 'BAD_TEMPLATE']);

export function splitRefsWarnings(warnings: string[] | null | undefined): { blocking: string[]; notes: string[] } {
  const blocking: string[] = [];
  const notes: string[] = [];
  for (const w of warnings || []) (BLOCKING_WARNINGS.has(w) ? blocking : notes).push(w);
  return { blocking, notes };
}

function refsWarningText(w: string): string {
  if (w.startsWith('SHEET_MISSING:')) return `Nhân vật "${w.slice('SHEET_MISSING:'.length)}" chưa có ảnh sheet nên bị bỏ khỏi ảnh tham chiếu — mặt có thể không khớp. Vẫn gen được.`;
  if (w === 'REFS_CAPPED_MAPS') return 'Quá nhiều ảnh sơ đồ: chỉ giữ tối đa 8 ảnh tham chiếu (bối cảnh + toàn bộ sheet nhân vật + sơ đồ), các sơ đồ cuối danh sách bị bỏ.';
  if (w === 'MAP_NO_IMAGE') return 'Có khung dùng sơ đồ chưa có ảnh nên sơ đồ đó bị bỏ khỏi ảnh tham chiếu — gen ảnh sơ đồ ở panel "Sơ đồ định vị" nếu muốn bố cục khớp. Vẫn gen được.';
  if (w === 'NO_PLATE') return 'Bối cảnh của video chưa có ảnh plate — gen ảnh bối cảnh trước.';
  if (w === 'TOO_MANY_CHARACTERS') return 'Quá nhiều nhân vật có sheet trong các khung (ảnh bối cảnh + sheet tối đa 8 ảnh) — bớt nhân vật khỏi các khung.';
  if (w === 'NOT_FOUND') return 'Chưa có mẫu prompt "film_storyboard_sheet" trong DB — cần áp SQL P3-1 (Leader).';
  if (w === 'BAD_TEMPLATE') return 'Mẫu prompt "film_storyboard_sheet" đang tắt.';
  return w;
}

export const refsWarningLines = (warnings: string[] | null | undefined): string[] => (warnings || []).map(refsWarningText);

// ───────────────────────── Giá + điều kiện bấm nút ─────────────────────────

const DEFAULT_COST = { low: 0.2, high: 0.3 };
const fmtUsd = (n: number): string => n.toFixed(2).replace('.', ',');

/** Giá ước hiện cạnh nút Gen. `~$0,20–0,30` (đo spike: Sunburst $0,21–0,22/tấm). */
export function storyboardCostLabel(est?: { low: number; high: number } | null): string {
  const low = est && Number.isFinite(est.low) && est.low > 0 ? est.low : DEFAULT_COST.low;
  const high = est && Number.isFinite(est.high) && est.high > 0 ? Math.max(est.high, low) : DEFAULT_COST.high;
  return high === low ? `~$${fmtUsd(low)}` : `~$${fmtUsd(low)}–${fmtUsd(high)}`;
}

/** Nút Gen khoá khi nào + vì sao (hiện ở tooltip). Tấm được gen từ bản khung ĐÃ LƯU ⇒ nháp chưa lưu cũng khoá. */
export function canGenerateSheet(sb: Pick<Storyboard, 'refsWarnings'> | null | undefined, st: { running?: boolean; dirty?: boolean }): { ok: boolean; reason?: string } {
  if (!sb) return { ok: false, reason: 'Chưa có khung — bấm "AI soạn" trước.' };
  if (st.running) return { ok: false, reason: 'Đang chạy ở nền.' };
  if (st.dirty) return { ok: false, reason: 'Có chỉnh sửa chưa lưu — lưu khung trước khi gen (tấm được gen từ bản đã lưu).' };
  const { blocking } = splitRefsWarnings(sb.refsWarnings);
  if (blocking.length) return { ok: false, reason: refsWarningText(blocking[0]) };
  return { ok: true };
}

/** Nút Lưu: PUT chạy dưới khoá việc của event ⇒ đang soạn/gen thì server trả 409 busy — khoá luôn từ đầu. */
export function canSaveFrames(st: { dirty: boolean; running: boolean }): { ok: boolean; reason?: string } {
  if (st.running) return { ok: false, reason: 'Đang soạn/gen ở nền — đợi xong rồi lưu (nếu không sẽ bị 409).' };
  if (!st.dirty) return { ok: false, reason: 'Chưa có thay đổi.' };
  return { ok: true };
}

/**
 * "AI soạn": khi nào hỏi xác nhận và có gửi `confirmReplace` không.
 * `confirmReplace:true` CHỈ khi storyboard đang EDITED/AGENT (BE trả 409 STORYBOARD_EDITED nếu thiếu); nháp chưa lưu trên bảng thì hỏi nhưng không cần cờ.
 */
export function composeConfirm(status: StoryboardAuthorStatus | null | undefined, dirty: boolean): { needed: boolean; confirmReplace: boolean; lines: string[] } {
  const lines: string[] = [];
  const confirmReplace = status === 'EDITED' || status === 'AGENT';
  if (status === 'EDITED') lines.push('Khung hiện tại đã được người sửa tay. AI soạn lại sẽ ghi đè toàn bộ khung (tấm đã gen vẫn giữ nhưng sẽ thành "cũ").');
  if (status === 'AGENT') lines.push('Khung hiện tại do agent (AI ngoài) tạo/sửa. AI soạn lại sẽ ghi đè toàn bộ khung (tấm đã gen vẫn giữ nhưng sẽ thành "cũ").');
  if (dirty) lines.push('Các chỉnh sửa chưa lưu trên bảng sẽ mất.');
  return { needed: lines.length > 0, confirmReplace, lines };
}

// ───────────────────────── Bản nháp bảng khung ─────────────────────────

export type FramePatch = Partial<Pick<StoryboardFrame, 'shotSize' | 'angle' | 'cameraMove' | 'who' | 'action' | 'positionMapId'>>;

export const cloneFrames = (frames: StoryboardFrame[]): StoryboardFrame[] => frames.map((f) => ({
  ...f, characterIds: [...f.characterIds], sourceShotIds: [...f.sourceShotIds], sourcePhaseOrders: [...f.sourcePhaseOrders],
}));

/**
 * Body `frames[]` của PUT: đủ 13 trường (+ `viewKey`, `subjectFacing` khi khung đã gán), `who`/`action` luôn là chuỗi, `positionMapId` null khi không có, giờ là number, mảng GIỮ NGUYÊN thứ tự
 * (`framesVersion` băm theo thứ tự — Leader rule #1), KHÔNG cắt khoảng trắng (BE so/ghi nguyên văn), không rò trường lạ (vd `id`).
 */
export function framesPayload(frames: StoryboardFrame[]): StoryboardFrame[] {
  return frames.map((f) => ({
    frameNo: Number(f.frameNo),
    groupNo: Number(f.groupNo),
    startS: Number(f.startS),
    endS: Number(f.endS),
    shotSize: f.shotSize,
    angle: f.angle,
    cameraMove: f.cameraMove,
    characterIds: [...(f.characterIds || [])],
    who: typeof f.who === 'string' ? f.who : '',
    action: typeof f.action === 'string' ? f.action : '',
    positionMapId: f.positionMapId ?? null,
    sourceShotIds: [...(f.sourceShotIds || [])],
    sourcePhaseOrders: [...(f.sourcePhaseOrders || [])],
    // 261010 P5a: chuyển tiếp `viewKey` để sửa khung trên UI không xoá âm thầm việc gán hướng (BE coi vắng = null). Khung chưa gán hướng giữ đúng 13 trường.
    ...(f.viewKey ? { viewKey: f.viewKey } : {}),
    // 261010 P5b: tương tự viewKey (kể cả 'none' — là dữ liệu đã gán).
    ...(f.subjectFacing ? { subjectFacing: f.subjectFacing } : {}),
  }));
}

export const framesEqual = (a: StoryboardFrame[], b: StoryboardFrame[]): boolean => JSON.stringify(framesPayload(a)) === JSON.stringify(framesPayload(b));

export const patchFrame = (frames: StoryboardFrame[], frameNo: number, patch: FramePatch): StoryboardFrame[] =>
  frames.map((f) => (f.frameNo === frameNo ? { ...f, ...patch } : f));

/** Giờ là của NHÓM: ghi cùng một cặp start/end cho mọi khung trong nhóm. */
export const setGroupTime = (frames: StoryboardFrame[], groupNo: number, startS: number, endS: number): StoryboardFrame[] =>
  frames.map((f) => (f.groupNo === groupNo ? { ...f, startS, endS } : f));

/** Đổi nhóm của một khung: nếu nhóm đích đã có khung khác thì nhận giờ của nhóm đó; chưa có thì giữ giờ cũ (người sửa tiếp). */
export function setFrameGroup(frames: StoryboardFrame[], frameNo: number, groupNo: number): StoryboardFrame[] {
  const other = frames.find((f) => f.frameNo !== frameNo && f.groupNo === groupNo);
  return frames.map((f) => (f.frameNo === frameNo
    ? { ...f, groupNo, ...(other ? { startS: other.startS, endS: other.endS } : {}) }
    : f));
}

export interface DraftGroup { groupNo: number; startS: number; endS: number; frameNos: number[] }

/** Nhóm của bản nháp (giờ lấy từ khung đầu của nhóm), thứ tự nhóm tăng dần. */
export function groupsOfDraft(frames: StoryboardFrame[]): DraftGroup[] {
  const byGroup = new Map<number, DraftGroup>();
  for (const f of [...frames].sort((a, b) => a.frameNo - b.frameNo)) {
    const g = byGroup.get(f.groupNo);
    if (g) g.frameNos.push(f.frameNo);
    else byGroup.set(f.groupNo, { groupNo: f.groupNo, startS: f.startS, endS: f.endS, frameNos: [f.frameNo] });
  }
  return Array.from(byGroup.values()).sort((a, b) => a.groupNo - b.groupNo);
}

// ───────────────────────── Kiểm trước PUT ─────────────────────────

const sameTime = (a: number, b: number): boolean => Math.abs(a - b) < SAME_TIME_EPS;
const fmtS = (n: number): string => String(Math.round(n * 100) / 100).replace('.', ',');

/**
 * Kiểm bản nháp TRƯỚC khi gửi — khuôn `validateFrames` của BE nhưng CHỈ những luật FE đánh giá được chính xác với thứ người sửa được:
 * số khung, số thứ tự, nhóm, giờ, từ vựng máy, độ dài chữ. KHÔNG kiểm shot/giai đoạn/nhân vật nguồn (không sửa được) và KHÔNG kiểm sơ đồ
 * (BE kiểm theo `ctx.links`, FE chỉ có `links.valid` ⇒ chặn nhầm một giá trị đã lưu hợp lệ) — BE trả 400 BAD_FRAMES nếu có.
 */
export function validateDraft(frames: StoryboardFrame[], ctx: { genDurationS?: number | null }): { ok: boolean; errors: string[] } {
  const errors: string[] = [];
  const add = (text: string) => { if (!errors.includes(text)) errors.push(text); };
  if (frames.length < FRAME_MIN || frames.length > FRAME_MAX) add(`Số khung phải từ ${FRAME_MIN} đến ${FRAME_MAX} (đang có ${frames.length}).`);
  const ordered = [...frames].sort((a, b) => a.frameNo - b.frameNo);
  const byGroup = new Map<number, StoryboardFrame[]>();
  let prevGroup: number | undefined;
  ordered.forEach((f, i) => {
    const at = `Khung ${f.frameNo}`;
    if (f.frameNo !== i + 1) add('Số thứ tự khung phải liên tiếp từ 1.');
    if (!Number.isInteger(f.groupNo) || f.groupNo < 1 || f.groupNo > GROUP_MAX) add(`${at}: số nhóm phải từ 1 đến ${GROUP_MAX}.`);
    byGroup.set(f.groupNo, [...(byGroup.get(f.groupNo) || []), f]);
    if (prevGroup !== undefined && (f.groupNo < prevGroup || f.groupNo > prevGroup + 1)) add(`${at}: số nhóm không được giảm hoặc nhảy cóc (nhóm liền trước là ${prevGroup}).`);
    prevGroup = f.groupNo;
    if (!SHOT_SIZE_SET.has(f.shotSize)) add(`${at}: cỡ cảnh "${f.shotSize}" không có trong từ vựng.`);
    if (!ANGLE_SET.has(f.angle)) add(`${at}: góc máy "${f.angle}" không có trong từ vựng.`);
    if (!MOVE_SET.has(f.cameraMove)) add(`${at}: chuyển động máy "${f.cameraMove}" không có trong từ vựng.`);
    const action = typeof f.action === 'string' ? f.action : '';
    if (!action.trim()) add(`${at}: hành động không được để trống.`);
    else if (action.length > ACTION_MAX) add(`${at}: hành động dài ${action.length} ký tự (tối đa ${ACTION_MAX}).`);
    const who = typeof f.who === 'string' ? f.who : '';
    if (who.length > WHO_MAX) add(`${at}: phần "ai có mặt" dài ${who.length} ký tự (tối đa ${WHO_MAX}).`);
  });
  const groupNos = Array.from(byGroup.keys()).sort((a, b) => a - b);
  if (!groupNos.length || groupNos.some((g, i) => g !== i + 1)) add('Số nhóm phải liên tiếp từ 1.');
  let prevEnd: number | undefined;
  for (const g of groupNos) {
    const group = byGroup.get(g)!;
    const first = group[0];
    if (group.some((f) => !sameTime(f.startS, first.startS) || !sameTime(f.endS, first.endS))) add(`Nhóm ${g}: các khung cùng nhóm phải cùng giờ.`);
    if (!(first.startS >= 0) || !(first.endS > first.startS)) add(`Nhóm ${g}: giờ kết thúc (${fmtS(first.endS)}s) phải sau giờ bắt đầu (${fmtS(first.startS)}s).`);
    if (prevEnd === undefined) {
      if (!sameTime(first.startS, 0)) add('Nhóm đầu phải bắt đầu từ 0s.');
    } else if (!sameTime(first.startS, prevEnd)) add(`Nhóm ${g}: phải bắt đầu đúng lúc nhóm trước kết thúc (${fmtS(prevEnd)}s), đang là ${fmtS(first.startS)}s.`);
    prevEnd = first.endS;
  }
  const dur = ctx.genDurationS;
  if (typeof dur === 'number' && Number.isFinite(dur) && prevEnd !== undefined && Math.abs(prevEnd - dur) > DURATION_EPS) {
    add(`Nhóm cuối phải kết thúc ở khoảng ${fmtS(dur)}s (thời lượng gen của video, sai số ${fmtS(DURATION_EPS)}s), đang là ${fmtS(prevEnd)}s.`);
  }
  return { ok: errors.length === 0, errors };
}

// ───────────────────────── Ô "sơ đồ" của một khung ─────────────────────────

export interface MapOption { value: string; label: string; eligible: boolean }

/**
 * Sơ đồ chọn được cho một khung — khuôn BE `validateFrames`: map phải được gán CHO event này ở ít nhất một giai đoạn nguồn của khung.
 * Khung không có giai đoạn nguồn (event không có phases) ⇒ không map nào hợp lệ (liên kết cả-video không tính).
 * Giá trị ĐÃ LƯU mà không còn hợp lệ vẫn được thêm vào cuối (đánh dấu) để người thấy và đổi, không biến mất âm thầm.
 */
export function mapOptionsFor(
  frame: Pick<StoryboardFrame, 'sourcePhaseOrders' | 'positionMapId'>,
  eventId: string,
  links: Array<{ eventId: string; phaseOrder: number | null; positionMapId: string }>,
  maps: Array<{ id: string; name: string }>,
): MapOption[] {
  const nameOf = (id: string): string => maps.find((m) => m.id === id)?.name || `(setup ${id.slice(0, 6)}…)`;
  const ids: string[] = [];
  for (const l of links) {
    if (l.eventId !== eventId || l.phaseOrder === null || !frame.sourcePhaseOrders.includes(l.phaseOrder)) continue;
    if (!ids.includes(l.positionMapId)) ids.push(l.positionMapId);
  }
  const out: MapOption[] = ids.map((id) => ({ value: id, label: nameOf(id), eligible: true }));
  const cur = frame.positionMapId;
  if (cur && !ids.includes(cur)) out.push({ value: cur, label: `${nameOf(cur)} (không còn hợp lệ cho khung này)`, eligible: false });
  return out;
}

// ───────────────────────── Câu lỗi tiếng Việt ─────────────────────────

const CODE_TEXT: Record<string, string> = {
  NOT_SKY_PLAN: 'storyboard chỉ dùng cho plan dùng luồng mới.',
  NOT_FOUND: 'không tìm thấy (video có thể đã bị xoá hoặc mẫu prompt chưa có trong DB).',
  NO_SHOTS: 'video chưa có shot nào — dựng camera/shot trước rồi mới soạn khung được.',
  STORYBOARD_EDITED: 'storyboard này đã có người/agent sửa — cần xác nhận ghi đè (đã nạp lại, hãy bấm "AI soạn" lần nữa).',
  FRAMES_CHANGED: 'khung đã đổi ở nơi khác (hoặc AI vừa ghi) — đã nạp lại bản mới, chỉnh sửa chưa lưu của bạn bị bỏ; hãy xem lại rồi sửa lại.',
  NO_COMPOSITION: 'video chưa có khung — bấm "AI soạn" trước.',
  NO_PLATE: 'bối cảnh của video chưa có ảnh plate — gen ảnh bối cảnh trước.',
  TOO_MANY_CHARACTERS: 'quá nhiều nhân vật có sheet trong các khung (ảnh bối cảnh + sheet tối đa 8 ảnh) — bớt nhân vật khỏi các khung.',
  BAD_FRAMES: 'khung không hợp lệ.',
  BAD_TEMPLATE: 'mẫu prompt storyboard đang tắt.',
};

const BAD_FRAME_TEXT: Record<string, string> = {
  FRAME_COUNT: 'số khung phải từ 2 đến 10',
  FRAME_NO: 'số thứ tự khung phải liên tiếp từ 1',
  GROUP_NO: 'số nhóm phải từ 1 đến 4',
  GROUP_ORDER: 'số nhóm không được giảm hoặc nhảy cóc',
  SHOT_SIZE: 'cỡ cảnh không có trong từ vựng',
  ANGLE: 'góc máy không có trong từ vựng',
  CAMERA_MOVE: 'chuyển động máy không có trong từ vựng',
  ACTION: 'hành động trống hoặc quá 400 ký tự',
  WHO: 'phần "ai có mặt" quá 200 ký tự',
  SOURCE_SHOT: 'shot nguồn không còn thuộc video này',
  SOURCE_PHASE: 'giai đoạn nguồn không hợp lệ',
  CHARACTER: 'nhân vật không thuộc truyện',
  POSITION_MAP: 'sơ đồ chưa được gán cho video ở giai đoạn nguồn của khung',
  GROUP_SEQUENCE: 'số nhóm phải liên tiếp từ 1',
  GROUP_TIME: 'các khung cùng nhóm phải cùng giờ',
  TIME: 'giờ kết thúc phải sau giờ bắt đầu',
  TIME_START: 'nhóm đầu phải bắt đầu từ 0s',
  TIME_CONTIGUOUS: 'phải bắt đầu đúng lúc nhóm trước kết thúc',
  TIME_END: 'nhóm cuối phải kết thúc đúng thời lượng gen của video (sai số 0,5s)',
};
const FRAME_SCOPED = new Set(['FRAME_NO', 'GROUP_NO', 'GROUP_ORDER', 'SHOT_SIZE', 'ANGLE', 'CAMERA_MOVE', 'ACTION', 'WHO', 'SOURCE_SHOT', 'SOURCE_PHASE', 'CHARACTER', 'POSITION_MAP']);
const GROUP_SCOPED = new Set(['GROUP_TIME', 'TIME', 'TIME_CONTIGUOUS']);

/** Một mã chi tiết của BAD_FRAMES (`ACTION:2`, `GROUP_TIME:1`, `TIME_END`…) ⇒ câu tiếng Việt. Mã lạ giữ nguyên. */
export function badFrameText(code: string): string {
  const [name, arg] = String(code).split(':');
  const base = BAD_FRAME_TEXT[name];
  if (!base) return String(code);
  if (arg && FRAME_SCOPED.has(name)) return `Khung ${arg}: ${base}`;
  if (arg && GROUP_SCOPED.has(name)) return `Nhóm ${arg}: ${base}`;
  return base.charAt(0).toUpperCase() + base.slice(1);
}

/** Câu lỗi tiếng Việt cho envelope `{code, message, details?}` của route storyboard; không nhận ra mã ⇒ rơi về `apiErrorText` chung (kể cả 409 bận). */
export function storyboardErrorText(err: any, what: string): string {
  const d = err?.response?.data;
  const code = typeof d?.code === 'string' ? d.code : '';
  const base = CODE_TEXT[code];
  if (!base) return apiErrorText(err, what);
  let extra = '';
  if (code === 'BAD_FRAMES' && Array.isArray(d.details) && d.details.length) {
    extra = ` ${Array.from(new Set(d.details.map((x: unknown) => badFrameText(String(x))))).join('; ')}.`;
  }
  return `${what}: ${base}${extra}`;
}

export const isFramesChanged = (err: any): boolean => err?.response?.data?.code === 'FRAMES_CHANGED';
export const isStoryboardEdited = (err: any): boolean => err?.response?.data?.code === 'STORYBOARD_EDITED';

// ───────────────────────── 261010 P4-1 — nút Duyệt storyboard ─────────────────────────

/** Ảnh được duyệt là ảnh GỐC không chữ (`sheetRawUrl`) — đúng thứ BE đính vào video; có `sheetUrl` (bản dán chú thích) mà thiếu ảnh gốc thì vẫn khoá. */
export const storyboardApprovalView = (sb: Pick<Storyboard, 'sheetRawUrl' | 'staleness' | 'approval'>): ApprovalView =>
  approvalView(sb.approval, { hasImage: !!sb.sheetRawUrl, stale: sb.staleness === 'INPUT_CHANGED' });

export const storyboardApprovalErrorText = (err: any, what: string): string => approvalErrorText(err, what, storyboardErrorText);
